import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaCompass, FaRocket, FaLightbulb, FaMap } from "react-icons/fa";
import Bulb from '../../icons/launchHub/choosePath/Bulb';
import House from '../../icons/launchHub/choosePath/House';
import Building from '../../icons/launchHub/choosePath/Building';
import Meter from '../../icons/launchHub/choosePath/Meter';

gsap.registerPlugin(ScrollTrigger);

// Data definition
const PATH =  [
    { Icon : <Bulb className='w-12 h-12' /> , text : 'Idea to MVP Track'},
    { Icon : <House className='w-12 h-12' /> , text : 'GTM & Traction Track'},
    { Icon : <Building className='w-12 h-12' /> , text : 'Fundraising Readiness Track'},
    { Icon : <Meter className='w-10 h-10' /> , text : 'AI/Deeptech Acceleration Track'},
]

const ChooseYourPath = () => {
    const sectionRef = useRef(null);
    const splashRef = useRef(null);
    const contentRef = useRef(null);
    const cardsRef = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 75%", 
                    toggleActions: "play none none none",
                }
            });

            // 1. Splash Expand
            tl.fromTo(splashRef.current, 
                { clipPath: "circle(0% at 50% 50%)" }, 
                { 
                    clipPath: "circle(150% at 50% 50%)", 
                    duration: 1.2, 
                    ease: "expo.inOut"
                }
            );

            // 2. Title Float Up
            tl.fromTo(contentRef.current, 
                { y: 50, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }, 
                "-=0.7"
            );

            // 3. Cards Pop (Fixed using fromTo)
            // We use fromTo to ensure they don't get stuck at opacity 0
            tl.fromTo(cardsRef.current, 
                { scale: 0, y: 100, rotation: 10, opacity: 0 }, 
                { 
                    scale: 1, 
                    y: 0, 
                    rotation: 0, 
                    opacity: 1, 
                    duration: 1.2, 
                    stagger: 0.1, 
                    ease: "elastic.out(1, 0.5)" 
                }, 
                "-=0.6"
            );

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} className='relative py-12 md:py-20 flex justify-center items-center overflow-hidden min-h-[500px]'>
            
            {/* Background Splash Layer */}
            <div 
                ref={splashRef}
                className="absolute inset-0 bg-blue-600 z-0"
                // Set initial style to ensure it hides if JS fails
                style={{ clipPath: 'circle(0% at 50% 50%)' }} 
            ></div>

            <article className='relative z-10 flex flex-col gap-y-4 container max-w-7xl px-6 md:px-12'>
                <h2 
                    ref={contentRef} 
                    className="font-[600] text-[34px] sm:text-[40px] leading-[40px] -tracking-[1.2px] md:mb-10 mb-6 text-center text-white"
                >
                    Path To Build:
                </h2>
                
                <ol className='grid min-[500px]:grid-cols-2 min-[960px]:grid-cols-4 gap-5'>
                    {PATH.map((path, index) => (
                        <li 
                            key={index} 
                            // Safer Ref assignment: resets the array slot for this index
                            ref={(el) => (cardsRef.current[index] = el)}
                            className='rounded-[10px] p-8 pt-10 bg-[#F6F6F6] flex flex-col justify-center items-center gap-y-3 cursor-pointer transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl'
                        >
                            <span className='bg-white w-20 h-20 flex items-center justify-center rounded-full shadow-sm'>
                                {path.Icon}
                            </span>
                            <h6 className='font-[500] text-black text-[17px] min-[500px]:text-[16px] leading-[27px] text-center'>
                                {path.text}
                            </h6>
                        </li>
                    ))}
                </ol>
            </article>
        </section>
    );
}

export default ChooseYourPath;